import React, { createContext, useContext, useEffect, useState } from 'react';
import { AuthUser, clearStoredAuth, establishDcSession, fetchDcSession, getStoredToken, getStoredUser, loginWithCredentials, persistLawvoSession, reAuthWithToken } from '../data/auth';
import { getDcAccessToken, saveDcState } from '../data/lawvoStorage';
interface AuthContextType {
  user: AuthUser | null;
  accessToken: string | null;
  dcJwt: string | null;
  isAuthLoading: boolean;
  isAuthenticated: boolean;
  isDcAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshDcSession: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
async function bootstrapDcSession(user: AuthUser, forceRefresh = false): Promise<string | null> {
  try {
    const session = await establishDcSession(user, {
      forceRefresh
    });
    return session.jwt;
  } catch {
    return getDcAccessToken() || null;
  }
}
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser());
  const [accessToken, setAccessToken] = useState<string | null>(getStoredToken());
  const [dcJwt, setDcJwt] = useState<string | null>(getDcAccessToken() || null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  useEffect(() => {
    const validateToken = async () => {
      const token = getStoredToken();
      if (!token) {
        setIsAuthLoading(false);
        return;
      }
      try {
        const response = await reAuthWithToken(token);
        if (response.user.role !== 'super-admin') {
          clearStoredAuth();
          setUser(null);
          setAccessToken(null);
          setDcJwt(null);
          return;
        }
        persistLawvoSession(response);
        setUser(response.user);
        setAccessToken(response.accessToken);
        const jwt = await bootstrapDcSession(response.user, false);
        setDcJwt(jwt);
      } catch {
        clearStoredAuth();
        setUser(null);
        setAccessToken(null);
        setDcJwt(null);
      } finally {
        setIsAuthLoading(false);
      }
    };
    validateToken();
  }, []);
  const refreshDcSession = async () => {
    if (!user) return;
    const session = await fetchDcSession(user);
    saveDcState({
      user: session.user,
      jwt: session.jwt
    });
    setDcJwt(session.jwt);
  };
  const login = async (email: string, password: string) => {
    const response = await loginWithCredentials(email, password);
    if ('requires2FA' in response && response.requires2FA) {
      throw new Error('Two-factor authentication is required. Complete 2FA in the main LawVo app first.');
    }
    if ('accessToken' in response && 'user' in response) {
      if (response.user.role !== 'super-admin') {
        throw new Error('Access denied. Only super-admin users can use this form.');
      }
      persistLawvoSession(response);
      setUser(response.user);
      setAccessToken(response.accessToken);
      const jwt = await bootstrapDcSession(response.user, true);
      setDcJwt(jwt);
    }
  };
  const logout = () => {
    clearStoredAuth();
    setUser(null);
    setAccessToken(null);
    setDcJwt(null);
  };
  return <AuthContext.Provider value={{
    user,
    accessToken,
    dcJwt,
    isAuthLoading,
    isAuthenticated: !!accessToken,
    isDcAuthenticated: !!dcJwt,
    login,
    logout,
    refreshDcSession
  }} data-id="element-314">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}